import { db, ensureVisitorUuid, type NodeType, type VisitRecord, type BookmarkRecord } from "./db";

export async function recordVisit(input: Omit<VisitRecord, "id" | "ts"> & { ts?: number }) {
  await ensureVisitorUuid();
  const record: VisitRecord = {
    ts: input.ts ?? Date.now(),
    path: input.path,
    title: input.title,
    nodeType: input.nodeType,
    nodeId: input.nodeId,
    filters: input.filters,
  };
  // Dedupe: if the most recent visit is the same path within 5s, skip.
  const last = await db.visits.orderBy("ts").last();
  if (last && last.path === record.path && record.ts - last.ts < 5000) return;
  await db.visits.add(record);
  // Keep journey bounded (latest 500).
  const count = await db.visits.count();
  if (count > 500) {
    const overflow = count - 500;
    const oldest = await db.visits.orderBy("ts").limit(overflow).toArray();
    await db.visits.bulkDelete(oldest.map((v) => v.id!).filter(Boolean));
  }
}

export async function listVisits(limit = 200): Promise<VisitRecord[]> {
  return db.visits.orderBy("ts").reverse().limit(limit).toArray();
}

export async function clearVisits() {
  await db.visits.clear();
}

export async function listBookmarks(): Promise<BookmarkRecord[]> {
  return db.bookmarks.orderBy("ts").reverse().toArray();
}

export async function isBookmarked(nodeType: NodeType, nodeId: string): Promise<boolean> {
  const hit = await db.bookmarks.where("[nodeType+nodeId]").equals([nodeType, nodeId]).first();
  return !!hit;
}

export async function toggleBookmark(args: {
  nodeType: NodeType;
  nodeId: string;
  title: string;
  note?: string;
}): Promise<boolean> {
  const existing = await db.bookmarks
    .where("[nodeType+nodeId]")
    .equals([args.nodeType, args.nodeId])
    .first();
  if (existing) {
    await db.bookmarks.delete(existing.id!);
    return false;
  }
  await db.bookmarks.add({
    ts: Date.now(),
    nodeType: args.nodeType,
    nodeId: args.nodeId,
    title: args.title,
    note: args.note,
  });
  return true;
}

export async function updateBookmarkNote(id: number, note: string) {
  await db.bookmarks.update(id, { note });
}

export async function removeBookmark(id: number) {
  await db.bookmarks.delete(id);
}
